import { and, eq } from "drizzle-orm";
import {
  type PowerSyncDrizzleDB,
  purchase_order,
  purchase_order_line,
  supplier,
} from "@comandero/powersync";
import { livePurchaseOrderLineFilters } from "../live/filters";
import { nowISO } from "../utils/date-helpers";
import { patchRowVerified, type VerifiedWriteOptions } from "../powersync/verified-write";
import { generateId } from "@fleet/utils";
import { purchaseLineTotal, purchaseOrderTotal } from "./calc";

export type PurchaseOrderStatus = "draft" | "confirmed" | "received";

/** Creates a supplier. CC-7: write with await. */
export async function createSupplierMutation(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  name: string,
  opts?: { contact?: string | null; phone?: string | null },
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(supplier).values({
    id,
    organization_id: organizationId,
    name: name.trim(),
    contact: opts?.contact ?? null,
    phone: opts?.phone ?? null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function deleteSupplierMutation(
  db: PowerSyncDrizzleDB,
  supplierId: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, supplier, supplier.id, supplierId, { deleted_at: nowISO() }, options);
}

/** Creates a draft purchase order. */
export async function createPurchaseOrderMutation(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  opts?: { branchId?: string | null; supplierId?: string | null; note?: string | null },
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(purchase_order).values({
    id,
    organization_id: organizationId,
    branch_id: opts?.branchId ?? null,
    supplier_id: opts?.supplierId ?? null,
    status: "draft",
    total: 0,
    note: opts?.note ?? null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/**
 * Adds a line to a PO (ingredient, quantity, and unit cost) and recalculates
 * the PO total. PURE total calculation (calc.ts). CC-7: writes with await.
 */
export async function addPurchaseLineMutation(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  purchaseOrderId: string,
  ingredientId: string,
  qty: number,
  unitCost: number,
  options?: VerifiedWriteOptions,
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  const safeQty = Math.max(0, Math.trunc(qty));
  const safeCost = Math.max(0, Math.trunc(unitCost));
  const total = purchaseLineTotal({ qty: safeQty, unitCost: safeCost });
  await db.insert(purchase_order_line).values({
    id,
    organization_id: organizationId,
    purchase_order_id: purchaseOrderId,
    ingredient_id: ingredientId,
    qty: safeQty,
    unit_cost: safeCost,
    line_total: total,
    created_at: now,
    updated_at: now,
  });
  await recomputePurchaseOrderTotal(db, purchaseOrderId, options);
  return id;
}

/** Recalculates a PO's total from its lines (pure calculation). */
export async function recomputePurchaseOrderTotal(
  db: PowerSyncDrizzleDB,
  purchaseOrderId: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  const lines = await db
    .select()
    .from(purchase_order_line)
    .where(
      and(
        eq(purchase_order_line.purchase_order_id, purchaseOrderId),
        ...livePurchaseOrderLineFilters(),
      ),
    );
  const total = purchaseOrderTotal(
    lines.map((l) => ({ qty: l.qty ?? 0, unitCost: l.unit_cost ?? 0 })),
  );
  await patchRowVerified(
    db,
    purchase_order,
    purchase_order.id,
    purchaseOrderId,
    { total },
    options,
  );
}

/**
 * Changes a PO's status. When moving to `received`, the server-side write-path
 * raises ingredient stock IDEMPOTENTLY (the client does not write it;
 * see purchase_order_with_stock in core-rust). CC-7: write with await.
 */
export async function setPurchaseOrderStatusMutation(
  db: PowerSyncDrizzleDB,
  purchaseOrderId: string,
  status: PurchaseOrderStatus,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    purchase_order,
    purchase_order.id,
    purchaseOrderId,
    { status },
    options,
  );
}

/** Receives a PO: marks it `received` so the server raises stock. */
export async function receivePurchaseOrderMutation(
  db: PowerSyncDrizzleDB,
  purchaseOrderId: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await setPurchaseOrderStatusMutation(db, purchaseOrderId, "received", options);
}
